import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { loadStripe } from '@stripe/stripe-js';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    ArrowLeft, Check, Zap, Crown, AlertCircle, Stethoscope, CreditCard, Loader2, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

const stripePromise = STRIPE_KEY ? loadStripe(STRIPE_KEY) : null;

// Currency configuration based on region
const currencyConfig = {
    en: { code: 'USD', symbol: '$', rate: 1, locale: 'en-US' },
    fr: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'fr-FR' },
    de: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'de-DE' },
    es: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'es-ES' },
    pt: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'pt-PT' },
    ru: { code: 'RUB', symbol: '₽', rate: 92.50, locale: 'ru-RU' },
    ar: { code: 'AED', symbol: 'د.إ', rate: 3.67, locale: 'ar-AE' },
    lt: { code: 'EUR', symbol: '€', rate: 0.92, locale: 'lt-LT' },
    hu: { code: 'HUF', symbol: 'Ft', rate: 355, locale: 'hu-HU' },
    zh: { code: 'CNY', symbol: '¥', rate: 7.24, locale: 'zh-CN' },
    hi: { code: 'INR', symbol: '₹', rate: 83.12, locale: 'hi-IN' },
    sw: { code: 'KES', symbol: 'KSh', rate: 153.50, locale: 'sw-KE' },
};

// Base prices in USD - keys used for translation lookup
const basePlans = [
    { id: 'per_use', nameKey: 'payPerUse', basePrice: 1.99, periodKey: 'perCheck', descKey: 'payOnlyWhenNeeded', featureKeys: ['singleSymptomCheck', 'aiPoweredAssessment', 'shareWithDoctor', 'pdfExport'], icon: Zap, popular: false },
    { id: 'basic', nameKey: 'basicMonthly', basePrice: 19.99, periodKey: 'perMonth', descKey: 'unlimitedForIndividuals', featureKeys: ['unlimited', 'aiPoweredAssessment', 'shareWithDoctor', 'pdfExport', 'medicalHistoryTracking', 'photoUploadAnalysis'], icon: Stethoscope, popular: true },
    { id: 'premium', nameKey: 'premiumMonthly', basePrice: 49.99, periodKey: 'perMonth', descKey: 'fullAccessPremium', featureKeys: ['everythingInBasic', 'bookOnlineConsultations', 'requestPrescriptions', 'specialtyReferrals', 'prioritySupport'], icon: Crown, popular: false, noteKey: 'additionalFeesApply' }
];

export default function Subscription() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { language, t } = useLanguage();
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('stripe');
    const [loading, setLoading] = useState(false);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const paymentSectionRef = useRef(null);

    // Get currency based on user's language
    const currency = currencyConfig[language] || currencyConfig.en;
    
    // Format price in user's currency
    const formatPrice = (baseUsdPrice) => {
        const convertedPrice = baseUsdPrice * currency.rate;
        // Round to 2 decimal places, or whole numbers for currencies like HUF, KES
        const roundedPrice = currency.rate > 10 
            ? Math.round(convertedPrice) 
            : Math.round(convertedPrice * 100) / 100;
        
        return new Intl.NumberFormat(currency.locale, {
            style: 'currency',
            currency: currency.code,
            minimumFractionDigits: currency.rate > 10 ? 0 : 2,
            maximumFractionDigits: currency.rate > 10 ? 0 : 2,
        }).format(roundedPrice);
    };

    // Plans with localized prices and translated text
    const plans = basePlans.map(plan => ({
        ...plan,
        name: t(plan.nameKey) || plan.nameKey,
        period: t(plan.periodKey) || plan.periodKey,
        description: t(plan.descKey) || plan.descKey,
        features: plan.featureKeys.map(key => t(key) || key),
        note: plan.noteKey ? (t(plan.noteKey) || plan.noteKey) : undefined,
        price: plan.basePrice * currency.rate,
        formattedPrice: formatPrice(plan.basePrice),
    }));

    // Scroll to payment section when plan is selected
    const scrollToPayment = () => {
        if (paymentSectionRef.current) {
            paymentSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Handle plan selection with auto-scroll
    const handlePlanSelect = (planId) => {
        setSelectedPlan(planId);
        setTimeout(scrollToPayment, 100);
    };

    const fetchSubscriptionStatus = async () => {
        try {
            const response = await axios.get(`${API}/subscription/status`);
            setSubscriptionStatus(response.data);
        } catch (error) {
            console.error('Failed to fetch subscription status');
        }
    };

    const verifyStripePayment = async (sessionId) => {
        try {
            const response = await axios.get(`${API}/subscription/stripe/status/${sessionId}`);
            if (response.data.payment_status === 'paid') {
                toast.success('Payment successful! Your subscription is now active.');
                navigate('/dashboard');
            }
        } catch (error) {
            toast.error('Failed to verify payment');
        }
    };

    useEffect(() => {
        fetchSubscriptionStatus();
        
        // Check for successful Stripe payment
        const sessionId = searchParams.get('session_id');
        if (sessionId) {
            verifyStripePayment(sessionId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const handleStripeCheckout = async () => {
        if (!selectedPlan) return;
        setLoading(true);
        
        try {
            const response = await axios.post(`${API}/subscription/stripe/checkout`, {
                plan_type: selectedPlan,
                origin_url: window.location.origin,
                currency: currency.code.toLowerCase(),
                language: language
            });
            
            if (response.data.checkout_url) {
                window.location.href = response.data.checkout_url;
            }
        } catch (error) {
            toast.error('Failed to initiate checkout');
            setLoading(false);
        }
    };

    const createPayPalOrder = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${API}/subscription/paypal/create-order`, { plan_type: selectedPlan });
            return response.data.order_id;
        } catch (error) {
            toast.error('Failed to create order');
            setLoading(false);
            throw error;
        }
    };

    const onPayPalApprove = async (data) => {
        setLoading(true);
        try {
            await axios.post(`${API}/subscription/paypal/capture-order?order_id=${data.orderID}`);
            toast.success('Payment successful!');
            navigate('/dashboard');
        } catch (error) {
            toast.error('Failed to complete payment');
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background py-8 lg:py-12" data-testid="subscription-page">
            <div className="max-w-6xl mx-auto px-4">
                <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
                    <ArrowLeft className="w-4 h-4 mr-2" /> {t('backToDashboard') || 'Back to Dashboard'}
                </Button>
                
                <h1 className="font-heading text-3xl font-bold mb-2">{t('chooseYourPlan') || 'Choose Your Plan'}</h1>
                <p className="text-muted-foreground mb-4">{t('upgradeToContnue') || 'Upgrade to continue using MediGuide'}</p>
                
                {/* Currency indicator */}
                <div className="flex items-center gap-2 mb-6 text-sm text-muted-foreground">
                    <span>{t('pricesShownIn') || 'Prices shown in'}</span>
                    <Badge variant="outline">{currency.code} ({currency.symbol})</Badge>
                </div>

                {subscriptionStatus && (
                    <Card className="mb-8 border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
                        <CardContent className="p-4 flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-600" />
                            <p>
                                {subscriptionStatus.can_check 
                                    ? (subscriptionStatus.remaining === -1 
                                        ? (t('youHaveUnlimitedChecks') || 'You have unlimited checks remaining')
                                        : `${subscriptionStatus.remaining} ${t('checksRemaining') || 'checks remaining'}`)
                                    : (t('yourFreeTrialEnded') || 'Your free trial has ended. Choose a plan to continue.')}
                            </p>
                        </CardContent>
                    </Card>
                )}

                {/* Plans Grid */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                    {plans.map((plan) => {
                        const Icon = plan.icon;
                        const isSelected = selectedPlan === plan.id;
                        
                        return (
                            <Card 
                                key={plan.id}
                                className={`relative cursor-pointer transition-all ${isSelected ? 'border-primary ring-2 ring-primary/20' : 'hover:border-primary/50'} ${plan.popular ? 'md:-mt-4 md:mb-4' : ''}`}
                                onClick={() => handlePlanSelect(plan.id)}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                        <Badge className="bg-primary">{t('mostPopular') || 'Most Popular'}</Badge>
                                    </div>
                                )}
                                <CardHeader className="text-center pb-2">
                                    <div className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${plan.popular ? 'bg-primary text-white' : 'bg-primary/10'}`}>
                                        <Icon className={`w-6 h-6 ${plan.popular ? '' : 'text-primary'}`} />
                                    </div>
                                    <CardTitle>{plan.name}</CardTitle>
                                    <CardDescription>{plan.description}</CardDescription>
                                </CardHeader>
                                <CardContent className="text-center">
                                    <div className="mb-6">
                                        <span className="text-4xl font-bold">{plan.formattedPrice}</span>
                                        <span className="text-muted-foreground">{plan.period}</span>
                                    </div>
                                    <ul className="space-y-2 text-left mb-6">
                                        {plan.features.map((f, i) => (
                                            <li key={i} className="flex items-start gap-2 text-sm">
                                                <Check className="w-4 h-4 text-success mt-0.5" /><span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    {plan.note && <p className="text-xs text-muted-foreground mb-4">{plan.note}</p>}
                                    <Button className="w-full rounded-full" variant={isSelected ? 'default' : 'outline'}>
                                        {isSelected ? (t('selected') || 'Selected') : (t('selectPlan') || 'Select')}
                                    </Button>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* Payment Section */}
                {selectedPlan && (
                    <Card ref={paymentSectionRef} className="max-w-md mx-auto scroll-mt-8">
                        <CardHeader className="text-center">
                            <CardTitle>{t('completePayment') || 'Complete Payment'}</CardTitle>
                            <CardDescription>{t('choosePaymentMethod') || 'Choose your preferred payment method'}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Tabs value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-4">
                                <TabsList className="grid grid-cols-2">
                                    <TabsTrigger value="stripe"><CreditCard className="w-4 h-4 mr-2" />{t('payWithCard') || 'Card'}</TabsTrigger>
                                    <TabsTrigger value="paypal">PayPal</TabsTrigger>
                                </TabsList>

                                <TabsContent value="stripe">
                                    <Button onClick={handleStripeCheckout} disabled={loading} className="w-full rounded-full">
                                        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                                        {t('payWithCard') || 'Pay with Card'} - {plans.find(p => p.id === selectedPlan)?.formattedPrice}
                                    </Button>
                                    <p className="text-xs text-center text-muted-foreground mt-2">{t('securePayment') || 'Secure payment powered by Stripe'}</p>
                                </TabsContent>

                                <TabsContent value="paypal">
                                    <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID || '', currency: currency.code }}>
                                        <PayPalButtons
                                            style={{ layout: "vertical", shape: "pill" }}
                                            createOrder={createPayPalOrder}
                                            onApprove={onPayPalApprove}
                                        />
                                    </PayPalScriptProvider>
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Loading Overlay with Logo */}
            {loading && (
                <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
                    <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl shadow-xl flex flex-col items-center gap-4">
                        <img 
                            src="/logo192.png" 
                            alt="MediGuide" 
                            className="w-20 h-20 animate-pulse"
                        />
                        <div className="flex items-center gap-2">
                            <Loader2 className="w-5 h-5 animate-spin text-primary" />
                            <span className="text-lg font-medium">{t('processingPayment') || 'Processing payment...'}</span>
                        </div>
                        <p className="text-sm text-muted-foreground text-center max-w-xs">
                            {t('pleaseWait') || 'Please wait while we securely process your payment. Do not close this window.'}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
